import { NgIf } from "@angular/common";
import { ChangeDetectorRef, Component, input, model, NgZone, OnDestroy, OnInit, output, signal, ViewEncapsulation } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { LeafletModule } from "@asymmetrik/ngx-leaflet";
import { LeafletD3Module } from "@asymmetrik/ngx-leaflet-d3";
import { BiTranslateService } from "@globals/bi-translate";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { ColorEnum } from "@globals/enums/ColorEnum";
import { areStringsEqual, cloneObject, createAddressString, isStringNullOrEmpty } from "@globals/helper-functions";
import { GeoObject } from "@globals/openapi-models/model/geoObject";
import { MapEntryAddressStatusDto } from "@globals/openapi-models/model/mapEntryAddressStatusDto";
import { BiDialogBaseService } from "@globals/services/bi-dialog-base.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import L, { CircleMarker, FeatureGroup, LatLng, Map, MapOptions, MarkerClusterGroup, PopupOptions } from "leaflet";
import { DialogService } from "primeng/dynamicdialog";
import { debounce, filter, finalize, map, Observer, of, Subject, switchMap, take, tap, timer } from "rxjs";
import { AddressContactsInfoComponent } from "../dialog-content/bi-address-info/address-contacts-info/address-contacts-info.component";
import { EntryAddressInfoStatusComponent } from "../dialog-content/bi-address-info/address-info-status/entry-address-info-status.component";
import { AddressInfoComponentInputs } from "../dialog-content/bi-address-info/bi-address-info-base.component";
import { BiSpinnerModule } from "../spinner/bi-spinner.module";
import { IMapLegendItem } from "./bi-map-legend/IMapLegendItem";
import { BiMapService } from "./bi-map.service";
import { BiMapArea } from "./BiMapArea";
import { BiMapComponentAddressModel } from "./BiMapComponentAddressModel";
import { BiMapComponentAddressModelFactory } from "./BiMapComponentAddressModelFactory";
import { BiMapMarker } from "./BiMapMarker";
import { BiMapTools } from "./BiMapTools";
import "./leaflet-map-corrections";
import { BiMapBufferLinesHandlerService } from "./map-utility-services/bi-map-buffer-lines-handler.service";
import { BiMapMarkerService } from "./map-utility-services/bi-map-marker.service";
import { BiMapPropertiesHandlerService } from "./map-utility-services/bi-map-properties-handler.service";
import { LantmäterietMapSetupService } from "./map-utility-services/lantmäteriet-map-setup.service";
import { DrawChangeEventData } from "./types-and-models/drawChangeEventData";

@UntilDestroy()
@Component({
  selector: "bi-map",
  templateUrl: "./bi-map.component.html",
  styleUrls: ["./bi-map.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, LeafletD3Module, LeafletModule, BiSpinnerModule],
  providers: [LantmäterietMapSetupService, BiMapService, DialogService, BiMapBufferLinesHandlerService, BiMapPropertiesHandlerService, BiMapMarkerService],
  host: {
    "class.map-height-transition": "useMapHeightTransition()"
  }
})
export class BiMapComponent implements OnInit, OnDestroy {
  //#region Properties
  public mapOptions: MapOptions;
  public leafletPingOptions = {
    duration: 15000,
    fps: 4,
    opacityRange: [1, 0],
    radiusRange: [25, 1]
  };

  /**
   * Whether Leaflet Draw feature will be added. Default true.
   */
  readonly enableDraw = model(true);
  readonly enableMarkerClusters = input(true);
  readonly maxClusterRadius = input(80);

  /**
   * Whether Leaflet Search feature will be added
   */
  readonly enableSearch = input(true);

  readonly showScale = input(false);

  readonly mapHeight = model<string>();

  readonly useMapHeightTransition = model(false);

  readonly adjustMapHeightToFitAvailableSpace = input(false);

  /**
   * This is the id of the parent element that wraps the map. This is used to calculate the height of the map.
   * This only need to be set if "adjustMapHeightToFitAvailableSpace" is set to true.
   */
  readonly mapWrapperId = input<string>();

  readonly defaultMapLayerId = input<number>(undefined);

  readonly reloadButtonClick = input<() => void>(undefined);

  public countryId = input.required<BiCountryId>();

  public leafletPingObserver: Observer<any>;

  public profileId = input<number>();

  public readonly isSuperAdmin = input(false);
  /**
   * Whether the map is to be used for driftstatus module. Default false.
   */
  public readonly isForDriftStatus = input(false);
  /**
   * Whether the search feature shouldn't be limited by pos lists.
   * Default true
   */
  public readonly noPosListSearch = input(true);

  /*
   * Determines if markers can be removed from the map.
   */
  readonly markersDeletable = model(true);
  readonly markersClickable = input(false);
  /*
   * Set this if caller is statuspage..
   */
  readonly isStatus = input(false);

  readonly customMarkerClickedEvent = input(false);

  readonly onlyProperties = input(false);

  /**
   * For Pin markers so that a Marker can cover a circle area with radius 15m
   */
  public singlePinMarkerRadius = input<number>(15);

  addressesDeleted = output<BiMapComponentAddressModel[]>();
  mapReady = output<Map>();

  polygonsOnMapChanged = output();

  /**
   * Fired when the list of selected addresses has been updated
   */
  addressesUpdated = output<BiMapComponentAddressModel[]>();

  markerClicked = output<BiMapMarker>();

  /**
   * Fired when selection/activation state of items has changed.
   * Event value is an array of number values being the IDs identifying the groups of map markers to show. (e.g. a list of profile IDs);
   * NOTE: for setting up legend feature, use method "setupLegend"
   */
  onLegendSelectionChanged = output<number[]>();

  private mapIsReady = signal<boolean>(false);

  private drawLayerGroup = new FeatureGroup();

  private markerClusterGroup: MarkerClusterGroup;
  private markerClusterGroupCritical: MarkerClusterGroup;

  public map: Map;
  public loading = signal(false);

  /**
   * The addresses selected on Map
   */
  private currentAddresses: Array<BiMapComponentAddressModel> = [];
  private kvhKeysOfDeletedAddresses: Array<string> = [];

  private deleteModeActive = signal(false);
  private editModeActive = false;

  private bodyResizeObserver: ResizeObserver;

  public get isEditing() {
    return this.editModeActive;
  }

  public mapReady$ = toObservable(this.mapIsReady);

  /**
   * Used to create a debounce effect for when shapes are created/edited/deleted
   */
  private drawChangedDebouncer = new Subject<DrawChangeEventData>();

  /**
   * List of circle markers, created by all manually drawn markers, to include in the auto fetch of address data.
   * This is for handling when user adds multiple markers before the debounce time expires.
   */
  private addedCircleMarkersForAutoFetch: Array<CircleMarker> = [];

  //#endregion

  constructor(
    private readonly mapService: BiMapService,
    private readonly translator: BiTranslateService,
    private readonly dialogService: DialogService,
    private readonly biDialogService: BiDialogBaseService,
    private readonly cd: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly bufferLinesHandler: BiMapBufferLinesHandlerService,
    private readonly propertiesHandler: BiMapPropertiesHandlerService,
    private readonly markerService: BiMapMarkerService
  ) {
    this.drawChangedDebouncer
      .pipe(
        untilDestroyed(this),
        tap(eventData => {
          // Single markers can be added multiple times in a row so collect them all.
          if (eventData.isSingleMarker) {
            // As we allow markers to cover a specific radius, we need to convert to CircleMarkers
            this.addedCircleMarkersForAutoFetch.push(...(eventData.layers as Array<L.Marker>).map(marker => new L.CircleMarker(marker.getLatLng(), { radius: null })));
            eventData.layers = this.addedCircleMarkersForAutoFetch;
          }
        }),
        debounce(eventData => timer(eventData.isSingleMarker ? 1500 : 500)),
        switchMap(layersAndType => {
          this.cd.markForCheck();
          return this.handleDrawChangeEvent(layersAndType);
        })
      )
      .subscribe(() => (this.addedCircleMarkersForAutoFetch = []));
  }

  public ngOnInit() {
    this.mapService
      .getGeneralMapSettings()
      .pipe(untilDestroyed(this), take(1))
      .subscribe(settings => {
        this.mapOptions = settings;
        this.cd.markForCheck();
      });

    if (!this.mapHeight()) this.mapHeight.set("900px");

    if (this.adjustMapHeightToFitAvailableSpace()) {
      this.setupBodyResizeObserver();
    }
  }

  public ngOnDestroy() {
    this.bodyResizeObserver?.unobserve(document.body);
  }

  /**
   * Eventhandler for NGX Leaflet's inbuild output property fired when map is initialized
   * @param map The full, initialized Leaflet Map object
   */
  public onMapReady(map: Map) {
    this.map = map;
    this.map.attributionControl.setPrefix('<a target="_blank" href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>');

    // As we're setting height dynamically and Leaflet map needs a height upon initialization, we
    // tell the map that it must redraw itself after a small delay
    setTimeout(() => {
      // If there are no map panes when the timeout is done, return. This is to avoid errors when the map is destroyed before the timeout is done.
      if (Object.values(map.getPanes()).length === 0) return;

      map.invalidateSize();
      this.fixAccessibilityIssues();
    }, 200);

    if (this.enableMarkerClusters()) {
      this.markerClusterGroup = this.markerService.createMarkerClusterGroup(this.maxClusterRadius());
      this.markerClusterGroupCritical = this.markerService.createMarkerClusterGroup(this.maxClusterRadius(), true);

      map.addLayer(this.markerClusterGroup).addLayer(this.markerClusterGroupCritical);
    }

    // When zooming from level < "disableClusteringAtZoom", we wanna unspiderfy the clusters as
    // it'll look weird if zooming in close while they're spiderfied
    this.map.on("zoomstart", () => this.handleZoomStart());

    this.mapService
      .setupLayersControl(this.map, this.isForDriftStatus(), this.defaultMapLayerId(), this.countryId(), this.profileId(), this.isSuperAdmin())
      .pipe(untilDestroyed(this), take(1))
      .subscribe(() => {
        if (this.countryId() === BiCountryId.SE) this.mapService.addSwedishMapOverlays(this.map);

        if (!this.isStatus() && this.enableDraw()) this.setupDrawControl();

        if (this.enableSearch()) setTimeout(() => this.mapService.setupSearchControl(this.map, this.countryId()), 0);

        const reloadButtonClick = this.reloadButtonClick();
        if (reloadButtonClick) this.mapService.setupReloadButton(reloadButtonClick);

        if (this.showScale()) this.mapService.setupScaleControl(this.map);

        this.mapReady.emit(map);
        this.mapIsReady.set(true);
      });
  }

  public disableMarkerRemoval() {
    this.markersDeletable.set(false);
  }

  private setupDrawControl() {
    this.mapService.setupDrawControl(this.map, this.drawLayerGroup, this.onPolygonsDeleted.bind(this));
    this.setupBufferingForPolylines();
    this.propertiesHandler.setupPropertyAreaManagement(this.map);

    this.map.on(L.Draw.Event.CREATED, (e: L.DrawEvents.Created) => {
      this.ngZone.run(() => {
        if (!this.mapService.isLayerPolyline(e.layer)) this.drawChangedDebouncer.next({ layers: [e.layer], changeType: "create", isSingleMarker: e.layer instanceof L.Marker });
      });
    });

    this.map.on(L.Draw.Event.EDITSTART, () => (this.editModeActive = true));

    this.map.on(L.Draw.Event.EDITED, (e: L.DrawEvents.Edited) => {
      this.ngZone.run(() => {
        const nonPolyLineLayers = e.layers.getLayers().filter(l => !this.mapService.isLayerPolyline(l));
        if (nonPolyLineLayers.length > 0) this.drawChangedDebouncer.next({ layers: nonPolyLineLayers, changeType: "edit", isSingleMarker: e.propagatedFrom instanceof L.Marker });
      });
    });
  }

  /**
   * Fired when a marker on map is clicked
   */
  private onMarkerClicked(marker: BiMapMarker) {
    if (this.customMarkerClickedEvent()) {
      this.markerClicked.emit(marker);
    } else {
      if (!(marker.address as BiMapComponentAddressModel).displayValue)
        (marker.address as BiMapComponentAddressModel).displayValue = createAddressString(
          marker.countryId,
          marker.address.zipcode,
          marker.address.city,
          marker.address.street,
          marker.address.number,
          marker.address.meters,
          marker.address.letter,
          undefined,
          undefined
        );
      this.openAddressInfoComponent(marker.address as BiMapComponentAddressModel);
    }
  }

  private openAddressInfoComponent(address: BiMapComponentAddressModel) {
    const isStatus = this.isStatus();
    if (isStatus) {
      this.dialogService.open(EntryAddressInfoStatusComponent, {
        data: <AddressInfoComponentInputs<MapEntryAddressStatusDto>>{
          address: BiMapComponentAddressModelFactory.constructByMapEntryAddressStatusDto(address, address.smsGroupId),
          smsGroupId: address.smsGroupId,
          enableDeselection: this.markersDeletable(),
          isStatus: isStatus,
          includeCompanyInfo: false,
          deleteAddress: () => this.deleteAddress(address),
          deleteProperty: () => this.deleteProperty(address)
        },
        header: address.displayValue,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true
      });
    } else {
      this.dialogService.open(AddressContactsInfoComponent, {
        data: <AddressInfoComponentInputs<BiMapComponentAddressModel>>{
          address: address,
          deleteAddress: () => this.deleteAddress(address),
          deleteProperty: () => this.deleteProperty(address)
        },
        header: address.displayValue,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true
      });
    }
  }

  private handleDrawChangeEvent(eventData: DrawChangeEventData) {
    if (eventData.changeType === "edit") {
      // Delete markers and addresses that are no longer in the edited layer(s)
      eventData.layers.forEach((editedShapeOrMarker: L.Polygon | L.Marker) => {
        // All markers belong to a shape/Polygon, and we can find that using the saved "parentPolygonId" on each BiMapMarker. We then know which markers are relevant including those
        // that might no longer be part/inside of the shape
        const layerId = L.stamp(editedShapeOrMarker);

        const markerOrPropertyBelongingToLayerFilter = (marker: BiMapMarker | BiMapArea) => marker.parentShapeId === layerId;

        // Find and remove markers that were belonging to the original polygon e.i. before edit) but not belonging to the new, edited one.
        let markersOrPropertiesBelongingToOriginalPolygon: Array<BiMapMarker | BiMapArea> = (this.markerClusterGroup.getLayers() as Array<BiMapMarker>).filter(markerOrPropertyBelongingToLayerFilter);
        if (markersOrPropertiesBelongingToOriginalPolygon.length > 0) {
          // Remove the markers that are no longer in the drawn shape
          this.markerService.findMarkersNotInLayer(editedShapeOrMarker, markersOrPropertiesBelongingToOriginalPolygon as Array<BiMapMarker>, this.singlePinMarkerRadius()).forEach(markerToRemove => {
            this.markerClusterGroup.removeLayer(markerToRemove);
            this.removeSelectedAddress((markerToRemove.address as BiMapComponentAddressModel).kvh);
          });
        }

        // Now do the same for critical markers/addresses
        markersOrPropertiesBelongingToOriginalPolygon = (this.markerClusterGroupCritical.getLayers() as Array<BiMapMarker>).filter(markerOrPropertyBelongingToLayerFilter);

        if (markersOrPropertiesBelongingToOriginalPolygon.length > 0) {
          this.markerService.findMarkersNotInLayer(editedShapeOrMarker, markersOrPropertiesBelongingToOriginalPolygon as Array<BiMapMarker>, this.singlePinMarkerRadius()).forEach(markerToRemove => {
            this.markerClusterGroupCritical.removeLayer(markerToRemove);
            this.removeSelectedAddress((markerToRemove.address as BiMapComponentAddressModel).kvh);
          });
        }

        // Now do the same for properties (matrikler)
        markersOrPropertiesBelongingToOriginalPolygon = this.propertiesHandler.getCurrentProperties().filter(markerOrPropertyBelongingToLayerFilter);

        if (markersOrPropertiesBelongingToOriginalPolygon.length > 0) {
          markersOrPropertiesBelongingToOriginalPolygon
            .filter((pa: BiMapArea) => this.hasBounds(editedShapeOrMarker) && !editedShapeOrMarker.getBounds().overlaps(pa.getBounds()))
            .forEach(propertyToRemove => this.propertiesHandler.removeProperty(propertyToRemove as BiMapArea));
        }
      });
    }

    return this.getAndShowAddressesByLayers(eventData.layers).pipe(
      tap(foundAddresses => {
        if (foundAddresses.length > 0) {
          this.addressesUpdated.emit(this.currentAddresses);
        }
      })
    );
  }

  private onPolygonsDeleted(layerGroup: L.LayerGroup) {
    this.deleteAddressesInLayerGroup(layerGroup);

    this.polygonsOnMapChanged.emit();
  }

  public deleteAddressesInLayerGroup(layerGroup: L.LayerGroup) {
    if (this.drawLayerGroup.getLayers().length === 0) {
      // Everything removed? Then remove all cached data

      this.markerClusterGroup.clearLayers();
      this.markerClusterGroupCritical.clearLayers();
      this.propertiesHandler.clearProperties();
      this.currentAddresses = [];
      this.bufferLinesHandler.clearBufferPolygons();
      this.addressesDeleted.emit([...this.currentAddresses]);
    } else {
      // Find and remove data inside each of the deleted polygons
      const addressMarkersToDelete: Array<BiMapMarker> = [];
      const criticalAddressMarkersToDelete: Array<BiMapMarker> = [];
      const propertiesInLayerToDelete: Array<BiMapArea> = [];

      layerGroup.eachLayer(layer => {
        const markerClusterGroupLayers = this.markerClusterGroup.getLayers() as BiMapMarker[];
        const markerClusterGroupCriticalLayers = this.markerClusterGroupCritical.getLayers() as BiMapMarker[];

        if (layer instanceof L.Marker) {
          addressMarkersToDelete.push(...markerClusterGroupLayers.filter(l => this.mapService.isMarkerInsidePinMarker(layer, l, this.singlePinMarkerRadius())));
          criticalAddressMarkersToDelete.push(...markerClusterGroupCriticalLayers.filter(l => this.mapService.isMarkerInsidePinMarker(layer, l, this.singlePinMarkerRadius())));
        } else {
          addressMarkersToDelete.push(...BiMapTools.getMarkersInsideShape(layer, markerClusterGroupLayers));
          criticalAddressMarkersToDelete.push(...BiMapTools.getMarkersInsideShape(layer, markerClusterGroupCriticalLayers));
        }

        const propertiesBelongingToLayer = this.propertiesHandler.getCurrentProperties().filter(p => p.parentShapeId === L.stamp(layer));
        if (propertiesBelongingToLayer.length > 0) propertiesInLayerToDelete.push(...propertiesBelongingToLayer);
      });

      // Delete markers and properties:
      addressMarkersToDelete.forEach(marker => this.markerClusterGroup.removeLayer(marker));

      criticalAddressMarkersToDelete.forEach(marker => this.markerClusterGroupCritical.removeLayer(marker));

      propertiesInLayerToDelete.forEach(marker => this.propertiesHandler.removeProperty(marker));

      // Now delete the addresses from the list of selected addresses
      const allMarkersAndPropertiesToDelete: Array<BiMapMarker | BiMapArea> = [...addressMarkersToDelete, ...criticalAddressMarkersToDelete, ...propertiesInLayerToDelete];
      if (allMarkersAndPropertiesToDelete.length > 0) {
        allMarkersAndPropertiesToDelete.forEach(m => {
          const idx = this.currentAddresses.findIndex(sa => areStringsEqual(sa.kvh, (m.address as BiMapComponentAddressModel).kvh));
          if (idx > -1) {
            this.currentAddresses.splice(idx, 1);
          }
        });

        this.addressesDeleted.emit(allMarkersAndPropertiesToDelete.map(m => m.address as BiMapComponentAddressModel));
      }
    }
  }

  //#region Helicopter route stuff (buffer lines)
  private setupBufferingForPolylines() {
    if (!this.map) throw new Error("Map not initialized!");

    this.bufferLinesHandler.setupBufferingForPolylines(this.map, this.deleteModeActive, this.drawLayerGroup);

    this.map.on(L.Draw.Event.EDITED, (e: L.DrawEvents.Edited) => {
      const deleteMarkersInPolygonFunc = (polygonId: number) => {
        this.removeMarkersAndAddressesInsideBufferPolygon(polygonId);
      };

      this.bufferLinesHandler.onMapDrawEdited(e, deleteMarkersInPolygonFunc.bind(this));
    });

    this.bufferLinesHandler.onBufferLineEdited.pipe(untilDestroyed(this)).subscribe(eventData => this.drawChangedDebouncer.next(eventData));

    this.map.on(L.Draw.Event.DELETESTART, () => this.deleteModeActive.set(true));

    this.map.on(L.Draw.Event.DELETESTOP, () => {
      this.deleteModeActive.set(false);
      this.bufferLinesHandler.onCancelDrawDelete(this.drawLayerGroup, deletedLinesAndBufferPolygons => this.getAndShowAddressesByLayers(deletedLinesAndBufferPolygons));
    });

    this.bufferLinesHandler.onBufferPolygonDeleted.pipe(untilDestroyed(this)).subscribe(polygon => {
      this.removeMarkersAndAddressesInsideBufferPolygon(L.stamp(polygon));
      this.map.removeLayer(polygon);
    });
  }

  private removeMarkersAndAddressesInsideBufferPolygon(bufferPolygonId: number) {
    this.bufferLinesHandler.getMarkersInsideBufferPolygon(this.markerClusterGroup, bufferPolygonId).forEach(marker => {
      this.markerClusterGroup.removeLayer(marker);
      const idx = this.currentAddresses.findIndex(a => areStringsEqual(a.kvh, (marker.address as BiMapComponentAddressModel).kvh));
      if (idx > -1) this.currentAddresses.splice(idx, 1);
    });

    this.bufferLinesHandler.getMarkersInsideBufferPolygon(this.markerClusterGroupCritical, bufferPolygonId).forEach(marker => {
      this.markerClusterGroupCritical.removeLayer(marker);
      const idx = this.currentAddresses.findIndex(a => areStringsEqual(a.kvh, (marker.address as BiMapComponentAddressModel).kvh));
      if (idx > -1) this.currentAddresses.splice(idx, 1);
    });
  }
  //#endregion

  private hasBounds(layer: L.Layer): layer is L.Layer & { getBounds(): L.LatLngBounds } {
    return (layer as L.Layer & { getBounds(): L.LatLngBounds }).getBounds !== undefined;
  }

  private removeSelectedAddress(addressKvh: string) {
    const idx = this.currentAddresses.findIndex(a => areStringsEqual(a.kvh, addressKvh));
    if (idx > -1) this.currentAddresses.splice(idx, 1);
  }

  private processAddressResult(addresses: Array<BiMapComponentAddressModel>, markerColor?: ColorEnum) {
    // Create marker for each of the addresses and add them to map
    const clusterMarkers: BiMapMarker[] = [];
    const criticalMarkers: BiMapMarker[] = [];
    const propertyAreaShapes: BiMapArea[] = [];

    addresses.forEach(address => {
      if (address.geo) {
        const mapArea = this.mapService.createLeafletGeoDataByGeoAddress(address);
        mapArea.on("click", () => this.openAddressInfoComponent(address));
        propertyAreaShapes.push(mapArea);
      } else {
        const marker = this.isStatus()
          ? this.mapService.createMarker(new L.LatLng(address.latitude, address.longitude), this.mapService.getMarkerColor(address.statusTypeId, address.isDelivered))
          : this.mapService.createAddressCircleMarker(new L.LatLng(address.latitude, address.longitude), address, address.isCriticalAddress, markerColor);

        marker.options.interactive = true;
        if (this.markersClickable()) {
          marker.on("click", () => this.onMarkerClicked(marker));
        }

        // Save the address on this marker so it can easily be found later if it should be deleted
        marker.address = address;
        marker.options.alt = address.displayValue ?? "...";
        // Set title to the address so that when hovering, the address displays in the small tooltip
        marker.options.title = marker.options.alt;
        marker.countryId = this.countryId();

        if (address.isCriticalAddress) {
          criticalMarkers.push(marker);
        } else {
          clusterMarkers.push(marker);
        }
      }
    });

    if (clusterMarkers.length > 0) this.markerClusterGroup.addLayers(clusterMarkers);

    if (criticalMarkers.length > 0) this.markerClusterGroupCritical.addLayers(criticalMarkers);

    // Add properties (matrikler)
    this.propertiesHandler.addPropertiesToMap(propertyAreaShapes, this.map);

    this.drawLayerGroup
      .getLayers()
      .concat(this.bufferLinesHandler.getBufferPolygons())
      .forEach((polygonOrPinMarker: L.Polyline | L.Polygon | L.Circle | BiMapMarker) => {
        clusterMarkers
          .concat(criticalMarkers)
          .filter(m => {
            return this.hasBounds(polygonOrPinMarker)
              ? polygonOrPinMarker.getBounds().contains(m.getLatLng())
              : this.mapService.isMarkerInsidePinMarker(polygonOrPinMarker, m, this.singlePinMarkerRadius());
          })
          .forEach(m => (m.parentShapeId = L.stamp(polygonOrPinMarker)));

        propertyAreaShapes.filter(pa => this.hasBounds(polygonOrPinMarker) && polygonOrPinMarker.getBounds().overlaps(pa.getBounds())).forEach(m => (m.parentShapeId = L.stamp(polygonOrPinMarker)));
      });
  }

  //#region METHODS FOR PARENT COMPONENT
  public fitToDrawLayers() {
    if (!this.isAnyLayerDrawn) return;
    const shapesGroup = new FeatureGroup();

    this.map.eachLayer(layer => {
      if (this.hasBounds(layer)) shapesGroup.addLayer(layer);
    });

    this.map.fitBounds(shapesGroup.getBounds(), { padding: [50, 50] });
  }

  public setupLegend(legendItems: Array<IMapLegendItem>, onSelectionChanged: (mapMarkerGroupIdentifierKeys: Array<number>) => void) {
    if (legendItems.length === 0) return;

    this.mapService.setupLegendControl(legendItems, onSelectionChanged);

    setTimeout(() => {
      const legendHeight = document.getElementById("bi-map-legend").getBoundingClientRect().height + "px";
      if (this.mapHeight().endsWith(")")) this.mapHeight.update(currentHeight => currentHeight.substring(0, currentHeight.length - 1) + ` - ${legendHeight})`);
      else this.mapHeight.update(currentHeight => `calc(${currentHeight} - ${legendHeight})`);
    }, 200);
  }

  public getAndShowAddressesByAllCurrentlyDrawnShapes() {
    // Get all shapes but filter away those that have an associated buffer shape
    this.currentAddresses = [];

    // Get all layers but filter away those that have an associated buffer shape (poly lines)
    let shapes: L.Layer[] = [];
    this.drawLayerGroup
      .getLayers()
      .filter(layer => !layer.isBuffered)
      .concat(this.bufferLinesHandler.getBufferPolygons())
      .forEach(layer => {
        if (layer instanceof L.Marker) {
          shapes.push(
            new L.Circle(layer.getLatLng(), {
              radius: null
            })
          );
        } else if (layer instanceof L.GeoJSON) {
          shapes = shapes.concat(layer.getLayers());
        } else {
          shapes.push(layer);
        }
      });

    this.markerClusterGroup.clearLayers();
    this.markerClusterGroupCritical.clearLayers(); // <-- Tilføj
    this.propertiesHandler.clearProperties();

    return this.getAndShowAddressesByLayers(shapes);
  }

  public getAndShowAddressesByLayers(layers: Array<L.Layer>) {
    this.loading.set(true);
    this.cd.detectChanges();
    const geoObjects = layers.map(l => this.getShapeFromLayer(l)).filter(o => o != undefined);

    if (geoObjects.length === 0) {
      this.loading.set(false);
      this.cd.detectChanges();
      return of(<BiMapComponentAddressModel[]>[]);
    }

    return this.mapService.getAddressesFromDraw(geoObjects, this.onlyProperties()).pipe(
      untilDestroyed(this),
      finalize(() => {
        this.loading.set(false);
        this.cd.detectChanges();
      }),
      // Filter away deleted addreses
      map(addresses => (this.kvhKeysOfDeletedAddresses?.length > 0 ? addresses.filter(a => this.kvhKeysOfDeletedAddresses.indexOf(a.kvh) === -1) : addresses)),
      // Filter away those already selected
      map(addresses => addresses.map(a => BiMapComponentAddressModelFactory.constructByMapEntryAddressDto(a))),
      tap(foundAddresses => {
        if (foundAddresses.length > 0) {
          if (this.currentAddresses.length > 0) {
            foundAddresses = foundAddresses.filter(a => {
              if (a.kvh) return !this.currentAddresses.some(sa => areStringsEqual(sa.kvh, a.kvh));

              if (a.geo) return !this.currentAddresses.some(sa => areStringsEqual(sa.displayValue, a.displayValue) && areStringsEqual(sa.geo, a.geo));

              return false;
            });
          }

          this.currentAddresses = this.currentAddresses.concat(foundAddresses);
          this.addressesUpdated.emit(this.currentAddresses);
          this.processAddressResult(foundAddresses);
        } else {
          // Clear addresses only if there's is only 1 shape drawn
          const numberOfLayers = this.drawLayerGroup.getLayers().length + this.bufferLinesHandler.getCurrentBufferPolygonsCount();
          if (numberOfLayers === 1) this.currentAddresses = [];
          this.biDialogService.openSimpleDismissable(this.translator.instant("messages.NoAccessibleAddressesFound"), undefined, undefined);
        }
      })
    );
  }

  /**
   * Get 'fresh' shapes that isn't part of any template
   */
  public getUnTemplatedShapes(): Array<L.Layer> {
    const layers = [];

    this.map.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.GeoJSON) {
        // Do nothing.. Template
      } else {
        // Only push the defined shapes..
        const shape = this.getShapeFromLayer(layer);
        if (shape) layers.push(shape);
      }
    });

    return layers;
  }

  private getShapeFromLayer(layer: L.Layer): GeoObject {
    if (layer instanceof L.Polygon) {
      // as we get an array of latLng arrays - and there will only be be 1
      return <GeoObject>{ latLngs: layer.getLatLngs()[0] as Array<L.LatLng> };
    } else if (layer instanceof L.CircleMarker) {
      return <GeoObject>{ latLngs: [layer.getLatLng()], radius: layer.getRadius() };
    } else if (layer instanceof L.Marker) {
      return <GeoObject>{ latLngs: [layer.getLatLng()] };
    }
  }

  private deleteAddressFromMap(address: BiMapComponentAddressModel) {
    // Remove it from map
    let mapAddressDataToRemove: L.Layer;

    if (!address.geo) {
      const addressKey = address.kvhx ?? address.kvh + (address.meters ?? "");

      const layers = address.isCriticalAddress ? this.markerClusterGroupCritical.getLayers() : this.markerClusterGroup.getLayers();

      mapAddressDataToRemove = layers.find((m: BiMapMarker) => m.address && (m.address.kvhx ?? (m.address as BiMapComponentAddressModel).kvh) === addressKey);
    } else mapAddressDataToRemove = this.propertiesHandler.getPropertyByAddressDisplayValue((address as BiMapComponentAddressModel).displayValue);

    if (mapAddressDataToRemove) {
      if (address.isCriticalAddress) {
        this.markerClusterGroupCritical.removeLayer(mapAddressDataToRemove);
      } else if (!address.geo) {
        this.markerClusterGroup.removeLayer(mapAddressDataToRemove);
      } else {
        this.propertiesHandler.removeProperty(mapAddressDataToRemove as BiMapArea);
      }
    }
  }

  /**
   * Removes layers from the map object (e.i. NOT layers added via Drawing)
   */
  public removeLayersFromMap(layers: Array<L.Layer>) {
    layers.forEach(layer => {
      this.map.removeLayer(layer);
    });
  }

  /**
   * Removes layers from the map's draw layer object (e.i. layers added via Leaflet Draw)
   */
  public removeDrawnLayers(layers: Array<L.Layer>) {
    layers.forEach(layer => {
      this.drawLayerGroup.removeLayer(layer);
    });
  }

  public clearPropertiesLayerGroup() {
    this.propertiesHandler.clearProperties();
  }

  /**
   * Removes markers that has been added to the map only.
   * @onlyMarkersNotInLayerGroup If true, only markers that are not in a layer group (e.i. draw layer group, clusterMarkers etc.) will be removed.
   */
  public removeMapMarkers(onlyMarkersNotInLayerGroup = true) {
    this.map.eachLayer(l => {
      if (l instanceof L.Marker) this.map.removeLayer(l);
    });
    if (!onlyMarkersNotInLayerGroup) {
      this.markerClusterGroup.clearLayers();
      this.markerClusterGroupCritical.clearLayers();
    }
  }

  public removeAllNonMarkerShapes() {
    this.map.eachLayer(layer => {
      if (layer instanceof L.Polygon || layer instanceof L.Polyline || layer instanceof L.Circle) this.map.removeLayer(layer);
    });
    this.drawLayerGroup.clearLayers();
    this.bufferLinesHandler.clearBufferPolygons();
    this.propertiesHandler.clearProperties();
  }

  /**
   * Deletes an address - both in the local list of selected addresses and on the map
   * @param emitEvent Whether to emit the "addressesDeleted" event to parent listeners
   */
  public deleteAddress(address: BiMapComponentAddressModel, emitEvent = true) {
    if (this.markersDeletable()) {
      // find this address among the selected ones
      const addressKvhxOrKvh = address.kvhx ?? (address as any).kvh + (address.meters ?? "");
      this.kvhKeysOfDeletedAddresses.push(address.kvh);
      const indexOfAddressToRemove = this.currentAddresses.findIndex(a => areStringsEqual(a.kvhx ?? a.kvh, addressKvhxOrKvh));
      if (indexOfAddressToRemove !== -1) {
        const deletedAddress = this.currentAddresses.splice(indexOfAddressToRemove, 1)[0];
        this.deleteAddressFromMap(deletedAddress);
        if (emitEvent) this.addressesDeleted.emit([deletedAddress]);
      }
    }
  }

  /**
   * Like "deleteAddress()" but deletes a property area,which is saved as a selected address.
   */
  public deleteProperty(property: BiMapComponentAddressModel, emitEvent = true) {
    if (this.markersDeletable()) {
      const indexOfAddressToRemove = this.currentAddresses.findIndex(a => a.geo && areStringsEqual(a.displayValue, property.displayValue));
      if (indexOfAddressToRemove !== -1) {
        const deletedAddress = this.currentAddresses.splice(indexOfAddressToRemove, 1)[0];
        this.deleteAddressFromMap(deletedAddress);
        if (emitEvent) this.addressesDeleted.emit([deletedAddress]);
      }
    }
  }

  /**
   * Returns the Leaflet Map object
   */
  public getMapObject(): L.Map {
    return this.map;
  }

  public disableToolbar() {
    this.enableDraw.set(false);
    return this;
  }

  /**
   * Helper for detecting whether any layer has been drawn on map
   */
  public get isAnyLayerDrawn() {
    return this.drawLayerGroup.getLayers().length > 0 || this.bufferLinesHandler.getCurrentBufferPolygonsCount() > 0;
  }

  /**
   * Returns the currently selected addresses on map
   */
  public getSelectedAddreses(): Array<BiMapComponentAddressModel> {
    return cloneObject(this.currentAddresses);
  }

  public changeLayer(mapLayerId: number) {
    this.mapService.changeLayer(mapLayerId, this.map);
  }

  /**
   * returns the map layers that are currently available for the map. We need this because it could change when the
   * "countryId" input property changes (new country => new layers)
   */
  public getAvailableMapLayers() {
    return this.mapService.mapLayers;
  }

  /**
   * Returns the currently selected map layer id
   */
  public getCurrentMapLayerId() {
    return this.mapService.currentMapLayerId;
  }

  /**
   * Creates a new marker cluster group and inserts the passed markers
   * If the map hasn't been initialized with "enableMarkerClusters" set to true, this can be used to cluster specific markers only.
   */
  public createMarkerClusterForMarkers(markers: BiMapMarker[], isCritical: boolean = false, disableClusteringAtZoom = 13) {
    let clusterGroup: MarkerClusterGroup | undefined;

    if (isCritical) {
      if (!this.markerClusterGroupCritical) {
        this.markerClusterGroupCritical = L.markerClusterGroup({
          disableClusteringAtZoom
        });
      }
      clusterGroup = this.markerClusterGroupCritical;
    } else {
      if (!this.markerClusterGroup) {
        this.markerClusterGroup = L.markerClusterGroup({
          disableClusteringAtZoom
        });
      }
      clusterGroup = this.markerClusterGroup;
    }

    clusterGroup.clearLayers();

    clusterGroup.addLayers(markers);

    clusterGroup.addTo(this.map);
  }

  private handleZoomStart(): void {
    const disableClusteringAtZoom = 13;

    if (this.map.getZoom() < disableClusteringAtZoom) {
      if (this.markerClusterGroup) {
        this.markerClusterGroup.unspiderfy();
      }
      if (this.markerClusterGroupCritical) {
        this.markerClusterGroupCritical.unspiderfy();
      }
    }
  }

  public createMarkerWithIconUrl(coordinates: LatLng, iconUrl: string, popupContent?: string, popupOptions?: PopupOptions, clickCallback?: () => void, hoverCallback?: () => void): BiMapMarker {
    const marker = this.mapService.createMarkerByIconUrl(coordinates, iconUrl, popupContent, popupOptions);
    // Set the click or hover functionality
    if (!isStringNullOrEmpty(popupContent) || clickCallback || hoverCallback) {
      marker.on("click", () => {
        if (!isStringNullOrEmpty(popupContent)) {
          marker.openPopup();
          this.checkAndHandlePopupAndLegendBoxOverlap();
        }

        if (clickCallback) clickCallback();
      });

      if (hoverCallback) {
        marker.on("mouseover", () => {
          if (!isStringNullOrEmpty(popupContent) && !marker.isPopupOpen()) marker.openPopup();
          hoverCallback();
        });
        marker.on("mouseout", () => {
          if (!isStringNullOrEmpty(popupContent) && marker.isPopupOpen()) marker.closePopup();
        });
      }
    }

    return marker;
  }

  /**
   * Creates and inserts a marker on map
   * @param popupContent If provided, a popup showing this content will be bound to the marker upon click or hover (depending on the other parameters passed). HTML is supported.
   * @param clickCallback Callback to fire when the marker is clicked.
   * @param hoverCallback Callback to fire when the marker is hovered.
   */
  public createAndInsertMarkerWithIconUrl(
    coordinates: LatLng,
    iconUrl: string,
    popupContent?: string,
    popupOptions?: PopupOptions,
    clickCallback?: () => void,
    hoverCallback?: () => void
  ): BiMapMarker {
    const marker = this.createMarkerWithIconUrl(coordinates, iconUrl, popupContent, popupOptions, clickCallback, hoverCallback);
    marker.addTo(this.map);
    return marker;
  }

  /**
   * Adds new markers and/or shapes represented by the addresses passed. They either contain lat/long info or GeoJson data.
   * NOTE: NOT to be used for status maps
   * @param markerColor For explicitly specifying color of the marker.
   */
  public addMapMarkersByAddresses(addresses: BiMapComponentAddressModel[], markerColor?: ColorEnum): void {
    this.mapReady$
      .pipe(
        filter(val => val == true),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.processAddressResult(addresses, markerColor);

        this.currentAddresses = addresses;

        this.fitToDrawLayers();
      });
  }

  /**
   * Adds makers to the map (will not be clustered as they're not added to the makercluster group)
   */
  public addMarkers(markers: Array<BiMapMarker>) {
    if (markers.length > 0) markers.forEach(m => this.map.addLayer(m));
  }

  public setCenterCoordinatesAndZoomByCountry(countryId: BiCountryId) {
    let coordinates: LatLng;
    let zoom: number;
    switch (countryId) {
      case BiCountryId.DK:
        coordinates = new L.LatLng(56.29368089320703, 11.513671875000002);
        zoom = 8;
        break;
      case BiCountryId.SE:
        coordinates = new L.LatLng(61.59594603934757, 18.259277343750004);
        zoom = 6;
        break;
      case BiCountryId.FI:
        coordinates = new L.LatLng(64.38744100860767, 28.542480468750004);
        zoom = 6;
        break;
      case BiCountryId.NO:
        coordinates = new L.LatLng(63.990556, 12.307778);
        zoom = 6;
        break;
    }

    if (this.map.getZoom() !== zoom) this.map.setZoom(zoom);

    // we need to add a little delay after setting the zoom so that map can set coordinates while having correct zoom level
    setTimeout(() => this.map.setView(coordinates), 500);
  }

  /**
   * Whether to show a toggle for hiding/showing WEB-messages from archive. This is only relevant when map is
   * used for showing WEB-messages.
   */
  public setupShowFromArchiveToggle(nrOfDaysBackInArchive: number, onToggledCallback: (showFromArchive: boolean) => void) {
    this.mapService.setupShowArchiveFeature(nrOfDaysBackInArchive, onToggledCallback);
  }

  public getGeoJsonFromLayers(): Array<any> {
    const geoJsonObjects = [];

    this.drawLayerGroup
      .getLayers()
      .filter(l => !l.isBuffered)
      .concat(...this.bufferLinesHandler.getBufferPolygons())
      .forEach((layer: L.Polygon | L.Polyline | L.Circle | L.Marker) => {
        const geoJson = layer.toGeoJSON();

        // Add Circle Radius And Attach to GeoJsonObject
        if (layer instanceof L.Circle) geoJson.properties.radius = layer.getRadius();

        geoJsonObjects.push(geoJson);
      });

    return geoJsonObjects;
  }

  /**
   * Creates Leaflet layers from the provided GeoJSON and adds them to the Leaflet draw layer group.
   * @returns The added layers
   */
  public createLayersFromGeoJson(geoJSONObjects: string): Array<L.Layer> {
    const layers = Array<L.Layer>();

    L.geoJSON(JSON.parse(geoJSONObjects), {
      pointToLayer: (feature, latLng) => {
        if (feature.properties.radius) return new L.Circle(latLng, feature.properties.radius);
        else return this.mapService.createMarker(latLng, ColorEnum.BLUE);
      },
      onEachFeature: (_feature, layer) => {
        this.drawLayerGroup.addLayer(layer);
        layers.push(layer);
      }
    });
    return layers;
  }

  public addObjectsToLayersFromGeoJson(geoJSONObjects: string): void {
    L.geoJSON(JSON.parse(geoJSONObjects), {
      pointToLayer: (feature, latLng) => {
        if (feature.properties.radius) return new L.Circle(latLng, feature.properties.radius);
        else return this.mapService.createMarker(latLng, ColorEnum.BLUE);
      },
      onEachFeature: feature => {
        if (feature.geometry.type === "Polygon") {
          const polygon = new L.Polygon(feature.geometry.coordinates.map(x => x.map(y => L.latLng(y[1], y[0]))));
          if (!this.drawLayerGroup.hasLayer(polygon)) {
            this.drawLayerGroup.addLayer(polygon);
          }
        } else if (feature.geometry.type === "Point" && feature.properties.radius) {
          const center = feature.geometry.coordinates as Array<number>;
          const circle = new L.Circle(L.latLng(center[1], center[0]), feature.properties.radius);
          if (!this.drawLayerGroup.hasLayer(circle)) {
            this.drawLayerGroup.addLayer(circle);
          }
        } else if (feature.geometry.type === "Point" && !feature.properties.radius) {
          const center = feature.geometry.coordinates as Array<number>;
          const point = this.mapService.createMarker(L.latLng(center[1], center[0]), ColorEnum.BLUE);
          if (!this.drawLayerGroup.hasLayer(point)) {
            this.drawLayerGroup.addLayer(point);
          }
        } else if (feature.geometry.type === "LineString") {
          const coordinates = feature.geometry.coordinates.map((coord: number[]) => L.latLng(coord[1], coord[0]));
          this.bufferLinesHandler.generateAndAddBufferPolygonByStartAndEndCoordinate(coordinates[0], coordinates[1], feature.properties?.bufferWidth);
        }
      }
    });

    this.fitToDrawLayers();
  }

  public clearDrawLayerGroup() {
    this.drawLayerGroup.clearLayers();
  }

  /**
   * Clears all cotnent on the map (e.i. reset)
   */
  public clearMap() {
    this.removeMapMarkers(false);
    this.removeAllNonMarkerShapes();
    this.clearPropertiesLayerGroup();
    this.currentAddresses = [];
  }
  //#endregion

  /**
   * Leaflet doesn't come with full accessibility support, so we use this function to setup some extra things.
   * Code inspiration found here: http://melmo.github.io/accessibility/code/dist/leaflet.html
   */
  private fixAccessibilityIssues() {
    // Remove the shadow pane (otherwise each shadow image is read out)
    $(".leaflet-shadow-pane").remove();
    // Prevent screen readers from reading out each map tile
    $(".leaflet-tile-container img, .leaflet-shadow-pane img").attr("role", "presentation").attr("alt", "");

    // Adjust the behaviour of popups so that focus is shifted to the popup content when it is opened, and returned to the icon when it is closed
    this.map.on("popupopen", (popup: L.PopupEvent) => {
      const leafletPopupEl$ = $(popup.popup.getElement());
      // shift focus to the popup when it opens
      leafletPopupEl$.find(".leaflet-popup-content").attr("tabindex", "-1").focus();

      // move the close button to the end of the popup content so screen readers reach it
      // after the main popup content, not before
      const closeIcon$ = $(popup.popup.getElement()).find(".leaflet-popup-close-button");
      const closeText = this.translator.instant("shared.Close");
      closeIcon$.attr("aria-label", closeText).attr("title", closeText);

      leafletPopupEl$.find(".leaflet-popup-close-button").remove();

      closeIcon$.on("click", e => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      leafletPopupEl$.append(closeIcon$);
    });

    // return focus to the icon we started from before opening the pop up
    this.map.on("popupclose", (popup: any) => {
      if (popup.popup_source) $(popup.popup_source._icon).focus();
    });
  }

  /**
   * Checks if the top position of the currently displayed popup on the map overlaps with the legend box or goes above the window and
   * handles this by moving the map center in the y-direction
   */
  private checkAndHandlePopupAndLegendBoxOverlap() {
    const checkAndMoveYFunc = () => {
      const popupContainerTopPos = $(".leaflet-popup.leaflet-zoom-animated")[0]?.getBoundingClientRect().top;
      const legendBox = $(".flex.legend-items-wrapper");
      // If the position is < 0, it means that it's above window
      if (legendBox.length > 0 || popupContainerTopPos < 0) {
        const projectedCenter = this.map.project(this.map.getCenter()); // convert center to x,y pixels (geometric)
        let yPositionMustBeAdjusted = false;

        if (legendBox.length > 0) {
          const legendBoxBottomPos = legendBox[0].getBoundingClientRect().bottom;

          if (popupContainerTopPos && popupContainerTopPos < legendBoxBottomPos) {
            projectedCenter.y -= legendBoxBottomPos + 20 - popupContainerTopPos; // adding 20px so that legend box bottom part is not aligning with the popup container's top border
            yPositionMustBeAdjusted = true;
          }
        } else if (popupContainerTopPos < 0) {
          yPositionMustBeAdjusted = true;
        }

        if (yPositionMustBeAdjusted) {
          this.map.setView(this.map.unproject(projectedCenter)); // convert back to geographical coordinates
        }
      }
      this.map.off("zoomend", checkAndMoveYFunc);
    };

    this.map.on("zoomend", checkAndMoveYFunc);
  }

  public setLeafletPingObserver(observer: Observer<any>) {
    this.leafletPingObserver = observer;
  }

  private initialMapHeightSet = false;

  public setMapHeightToUseAvailableSpace() {
    if (!this.initialMapHeightSet) {
      this.useMapHeightTransition.set(false);
      this.cd.detectChanges();
    }

    this.mapHeight.set(`calc(100vh - ${document.getElementById(this.mapWrapperId()).getBoundingClientRect().y}px - 1rem)`);
    this.map?.invalidateSize();

    if (!this.initialMapHeightSet) {
      this.initialMapHeightSet = true;
      this.useMapHeightTransition.set(true);

      this.cd.detectChanges();

      this.mapReady$
        .pipe(
          untilDestroyed(this),
          filter(x => x == true),
          take(1)
        )
        .subscribe(() => {
          setTimeout(() => this.fitToDrawLayers(), 500);
        });
    }
  }

  /**
   * Setup observer for body resize event that will set the height of the map to max possible
   * without resulting in scrollbar.
   */
  private setupBodyResizeObserver() {
    this.bodyResizeObserver = new ResizeObserver(() => {
      this.setMapHeightToUseAvailableSpace();
    });
    this.bodyResizeObserver.observe(document.body);
  }
}
